import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Check, X, Share2, Download, Trophy } from "lucide-react";

const POS = ["1st", "2nd", "3rd", "4th"];

const TeamCell = ({ team }) => (
  <span className="inline-flex items-center gap-1.5 min-w-0">
    {team?.crest && <img src={team.crest} alt="" className="w-4 h-4 object-contain flex-shrink-0" />}
    <span className="font-display tracking-wider truncate">{team?.code || team?.name || "—"}</span>
  </span>
);

const StatusBadge = ({ status, played, total }) => {
  if (status === "completed")
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-green-500/50 text-green-400 bg-green-500/10"><Check size={10}/> Completed</span>;
  if (status === "provisional")
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-yellow-400/40 text-yellow-300 bg-yellow-400/5">As it stands{played != null ? ` · ${played}/${total}` : ""}</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-zinc-700 text-zinc-500">Not started</span>;
};

const GroupCard = ({ g }) => (
  <div className="border border-white/10 bg-black/30 p-3" data-testid={`bracket-result-group-${g.group}`}>
    <div className="flex items-center justify-between mb-2.5">
      <span className="font-display text-lg uppercase">Group {g.group}</span>
      <div className="flex items-center gap-2">
        <StatusBadge status={g.status} played={g.played_matches} total={g.total_matches} />
        <span className={`font-display text-lg ${g.points > 0 ? "text-primary-accent" : "text-zinc-500"}`} data-testid={`bracket-result-pts-${g.group}`}>
          +{g.complete ? g.points : g.provisional_points}{!g.complete && <span className="text-[9px] text-zinc-500 ml-0.5">prov</span>}
        </span>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-x-3 text-xs">
      <div className="label-tag text-zinc-500 mb-1">Your picks</div>
      <div className="label-tag text-zinc-500 mb-1">{g.complete ? "Final" : "Current"}</div>
      {POS.map((p, i) => (
        <React.Fragment key={i}>
          <div className={`flex items-center gap-1.5 py-1 border-b border-white/5 ${g.correct?.[i] ? "text-green-400" : "text-zinc-300"}`}>
            <span className="text-zinc-600 w-7 flex-shrink-0">{p}</span>
            <TeamCell team={g.predicted?.[i]} />
            {g.correct?.[i] ? <Check size={11} className="text-green-400 flex-shrink-0 ml-auto"/> : <X size={11} className="text-zinc-700 flex-shrink-0 ml-auto"/>}
          </div>
          <div className="flex items-center gap-1.5 py-1 border-b border-white/5 text-zinc-400">
            <span className="text-zinc-600 w-7 flex-shrink-0">{p}</span>
            <TeamCell team={g.actual?.[i]?.team} />
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

const ThirdsCard = ({ bt }) => {
  const pts = bt.group_stage_complete ? bt.points : bt.provisional_points;
  const slots = Array.from({ length: 8 });
  return (
    <div className="border border-white/10 bg-black/30 p-3 mt-3" data-testid="bracket-result-thirds">
      <div className="flex items-center justify-between mb-2">
        <span className="font-display text-lg uppercase">Best 3rds · Pick 8</span>
        <div className="flex items-center gap-2">
          <StatusBadge status={bt.status} />
          <span className={`font-display text-lg ${pts > 0 ? "text-primary-accent" : "text-zinc-500"}`} data-testid="bracket-result-thirds-pts">
            +{pts}{!bt.group_stage_complete && <span className="text-[9px] text-zinc-500 ml-0.5">prov</span>}
          </span>
        </div>
      </div>
      <div className="text-[11px] text-zinc-500 mb-2 uppercase tracking-widest">{bt.correct_count}/8 correct · {bt.pts_each} pts each</div>
      <div className="grid grid-cols-2 gap-x-3 text-xs">
        <div className="label-tag text-zinc-500 mb-1">Your picks</div>
        <div className="label-tag text-zinc-500 mb-1">{bt.group_stage_complete ? "Qualified" : "Qualifying now"}</div>
        {slots.map((_, i) => {
          const pick = bt.predicted?.[i];
          const qual = bt.qualified?.[i];
          return (
            <React.Fragment key={i}>
              <div className={`flex items-center gap-1.5 py-1 border-b border-white/5 ${pick?.correct ? "text-green-400" : "text-zinc-300"}`}>
                <span className="text-zinc-600 w-6 flex-shrink-0">{i + 1}</span>
                {pick ? <TeamCell team={pick.team} /> : <span className="text-zinc-700">—</span>}
                {pick?.group && <span className="text-[9px] text-zinc-600">({pick.group})</span>}
                {pick && (pick.correct ? <Check size={11} className="text-green-400 flex-shrink-0 ml-auto"/> : <X size={11} className="text-zinc-700 flex-shrink-0 ml-auto"/>)}
              </div>
              <div className="flex items-center gap-1.5 py-1 border-b border-white/5 text-zinc-400">
                <span className="text-zinc-600 w-6 flex-shrink-0">{i + 1}</span>
                {qual ? <><TeamCell team={qual.team} />{qual.group && <span className="text-[9px] text-zinc-600">({qual.group})</span>}</> : <span className="text-zinc-700">—</span>}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

/** Per-group + best-3rds predicted-vs-actual results for one player's bracket. Self-fetching. */
export default function BracketGroupResults({ tournamentId, userId, showHeader = true, section = "all", showShare = false, tournamentName = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get(`/tournaments/${tournamentId}/bracket/group-results/${userId}`)
      .then(({ data }) => { if (on) setData(data); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [tournamentId, userId]);

  const handleDownload = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, skipFonts: true, backgroundColor: "#09090b" });
      const link = document.createElement("a");
      link.download = `jinxing-bracket-${(data?.name || "result").replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Bracket card downloaded — share away!");
    } catch {
      toast.error("Couldn't generate the image. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-zinc-500" data-testid="bracket-results-loading">Loading results…</div>;
  if (!data || !data.has_bracket || !data.groups?.length)
    return <div className="p-8 text-center text-zinc-500" data-testid="bracket-results-empty">No group predictions to show.</div>;

  const showGroups = section !== "thirds";
  const showThirds = section !== "groups" && data.best_thirds;
  const bt = data.best_thirds;

  return (
    <div data-testid="bracket-group-results">
      {(showHeader || showShare) && (
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="label-tag text-zinc-500">{section === "thirds" ? "Best 3rds Result" : "Group Stage Results"}</div>
            {showHeader ? <h3 className="font-display text-2xl uppercase">{data.name}</h3> : <div className="text-xs text-zinc-500">{data.name}</div>}
          </div>
          <div className="flex items-center gap-3">
            {showHeader && (
              <div className="text-right">
                <div className="font-display text-3xl text-primary-accent" data-testid="bracket-results-total">{data.total_points}</div>
                <div className="label-tag text-zinc-500">Bracket pts</div>
              </div>
            )}
            {showShare && (
              <button onClick={() => setShareOpen(true)} data-testid="bracket-share-btn" title="Share my bracket result"
                className="flex items-center gap-2 border border-zinc-800 text-zinc-300 hover:border-primary-accent hover:text-primary-accent px-3 py-2 text-xs uppercase tracking-widest">
                <Share2 size={14}/> Share
              </button>
            )}
          </div>
        </div>
      )}

      {showGroups && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.groups.map((g) => <GroupCard key={g.group} g={g} />)}
        </div>
      )}
      {showThirds && <ThirdsCard bt={bt} />}

      {shareOpen && (
        <div className="fixed inset-0 z-[210] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bracket-share-modal"
          onClick={(e) => { e.stopPropagation(); setShareOpen(false); }}>
          <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="label-tag text-zinc-400">Share Card Preview</div>
              <button data-testid="bracket-share-close" onClick={() => setShareOpen(false)}
                className="w-8 h-8 flex items-center justify-center border border-zinc-800 text-zinc-400 hover:border-white/30"><X size={14}/></button>
            </div>
            <div ref={cardRef} className="bg-zinc-950 grid-bg p-7 border border-white/10" data-testid="bracket-share-card">
              <div className="flex items-center gap-2.5 mb-6">
                <div className="w-8 h-8 bg-primary-accent flex items-center justify-center"><Trophy size={18}/></div>
                <span className="font-display text-lg tracking-wider">JINXING SPORTS</span>
              </div>
              <div className="label-tag mb-1">Bracket · Group Stage</div>
              <div className="font-display text-2xl uppercase tracking-wide leading-none mb-1">{data.name}</div>
              {tournamentName && <div className="text-zinc-500 text-xs uppercase tracking-widest mb-5">{tournamentName}</div>}
              <div className="flex items-end gap-4 mb-5">
                <div className="font-display text-7xl text-primary-accent leading-[0.85]">{data.total_points}</div>
                <div className="pb-1.5">
                  <div className="text-zinc-400 text-sm">bracket points</div>
                  {bt && <div className="font-display text-xl">{bt.correct_count}/8 best 3rds</div>}
                </div>
              </div>
              <div className="grid grid-cols-6 gap-1.5 mb-6">
                {data.groups.map((g) => (
                  <div key={g.group} className={`flex flex-col items-center justify-center py-1.5 border ${g.points > 0 || g.provisional_points > 0 ? "border-primary-accent/40" : "border-white/10"}`}>
                    <span className="font-display text-sm leading-none">{g.group}</span>
                    <span className={`text-[10px] font-display ${(g.complete ? g.points : g.provisional_points) > 0 ? "text-primary-accent" : "text-zinc-600"}`}>+{g.complete ? g.points : g.provisional_points}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 pt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Predict · Survive · Conquer · jinxingsports.com
              </div>
            </div>
            <button onClick={handleDownload} disabled={busy} data-testid="bracket-share-download"
              className="w-full mt-3 bg-primary-accent text-white py-3 font-display tracking-widest text-sm uppercase hover:bg-[var(--primary-hover)] disabled:opacity-50 flex items-center justify-center gap-2">
              <Download size={14}/> {busy ? "Generating…" : "Download Image"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
